import type firebase from 'firebase/compat/app'
import firebaseApp from 'src/firebase/firebaseApp'

// Local: `/advanced-api` via webpack proxy. Staging/prod: load-balancer URL (ADR-058).
const BASE_PATH = process.env.ADVANCED_API_BASE || '/advanced-api'

export class AdvancedApiError extends Error {
  readonly status: number

  // The service's fixed error code (`invalid_track_lead`), read for a 400 only.
  readonly code?: string

  constructor(status: number, message: string, code?: string) {
    super(message)
    this.name = 'AdvancedApiError'
    this.status = status
    this.code = code
  }
}

// A parser rejection reaches here too, and its body is not the service's `{ error }`.
const badRequestCode = async (response: Response): Promise<string | undefined> => {
  if (response.status !== 400) {
    return undefined
  }
  const body: unknown = await response.json().catch(() => undefined)
  const code = body && typeof body === 'object' ? (body as { error?: unknown }).error : undefined

  return typeof code === 'string' && /^[a-z_]+$/.test(code) ? code : undefined
}

export const isAdvancedUnauthorized = (error: unknown): boolean =>
  error instanceof AdvancedApiError && error.status === 401

// Firebase restores the session asynchronously, so `currentUser` is null on the first render after a reload.
const currentUser = async (): Promise<firebase.User | null> => {
  const user = firebaseApp.auth().currentUser
  if (user) {
    return user
  }

  return new Promise((resolve) => {
    const unsubscribe = firebaseApp.auth().onAuthStateChanged((nextUser) => {
      unsubscribe()
      resolve(nextUser)
    })
  })
}

type RequestOptions = {
  body?: unknown
  signal?: AbortSignal
}

const request = async (method: string, path: string, { body, signal }: RequestOptions = {}): Promise<Response> => {
  const user = await currentUser()

  if (!user) {
    throw new AdvancedApiError(401, 'not signed in')
  }

  const send = async (forceRefresh: boolean) =>
    fetch(`${BASE_PATH}${path}`, {
      method,
      headers: {
        authorization: `Bearer ${await user.getIdToken(forceRefresh)}`,
        ...(body === undefined ? null : { 'content-type': 'application/json' }),
      },
      ...(body === undefined ? null : { body: JSON.stringify(body) }),
      signal,
    })

  let response = await send(false)
  // Only a 401 is safe to resend: a stale token after an emulator restore or HMR.
  if (response.status === 401) {
    response = await send(true)
  }

  if (!response.ok) {
    // Only a 400's code is read: a 404 must stay indistinguishable from "not your patient" (ADR-016).
    throw new AdvancedApiError(
      response.status,
      `advanced api responded ${response.status}`,
      await badRequestCode(response)
    )
  }

  return response
}

export const advancedGet = async <T>(path: string, signal?: AbortSignal): Promise<T> =>
  (await request('GET', path, { signal })).json() as Promise<T>

export const advancedPost = async <T>(path: string, body: unknown): Promise<T> =>
  (await request('POST', path, { body })).json() as Promise<T>

export const advancedPut = async <T>(path: string, body: unknown): Promise<T> =>
  (await request('PUT', path, { body })).json() as Promise<T>

export const advancedDelete = async (path: string): Promise<void> => {
  await request('DELETE', path)
}
